/*
 * Do not remove or alter the notices in this preamble.
 * This software code regards iO Custom Software.
 * Copyright © 2026 iO The Netherlands B.V. and/or its affiliates.
 * www.iodigital.com All rights reserved. License grant and user rights and obligations
 * according to applicable license agreement. Please contact legal@iodigital.com for
 * questions regarding license and user rights.
 */

import { consume } from '@lit/context';
import { css, html, LitElement } from 'lit';
import { customElement } from 'lit/decorators.js';

import { ConfiguratorContextType, configuratorGlobal } from '@/context';
import { BREAKPOINT_EXTRA_LARGE_PX, BREAKPOINT_MEDIUM_PX } from '@/settings';
import { baseStyles, skeletonStyles } from '@/styles';
import { classMap } from 'lit/directives/class-map.js';

@customElement('fg-color-step-skeleton-loader')
export class FgColorStepSkeletonLoader extends LitElement {
  @consume({ context: configuratorGlobal.context, subscribe: true })
  configuratorContext!: ConfiguratorContextType;

  isNotCollapsible() {
    return this.configuratorContext.colorCollapsible !== 1;
  }

  itemCount() {
    return this.isNotCollapsible() ? 12 : 5;
  }

  render() {
    const collapsibleModifier = {
      'is-not-collapsible': this.isNotCollapsible(),
    };

    return html`
      <div class="skeleton-container ${classMap(collapsibleModifier)}">
        <div class="skeleton-header">
          <div class="skeleton-title"></div>
          <div class="skeleton-icon"></div>
        </div>
        <div class="skeleton-content">
          ${Array.from({ length: this.itemCount() }).map(
            () => html`
              <div class="skeleton-image-item">
                <div class="skeleton-item-image"></div>
                <div class="skeleton-item-label"></div>
              </div>
            `,
          )}
        </div>
      </div>
    `;
  }

  static styles = [
    baseStyles,
    skeletonStyles,
    css`
      :host {
        --skeleton-item-size-default: 80px;
        --skeleton-item-size-collapsible: 180px;
        --skeleton-item-size-xl: 96px;

        --skeleton-gap-default: 0.5rem;
        --skeleton-gap-collapsible: 10px;
        --skeleton-row-gap: 1.25rem;
      }

      .skeleton-header {
        margin-bottom: 1.5rem;
      }

      .skeleton-container.is-not-collapsible .skeleton-header {
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
        gap: var(--skeleton-gap-collapsible);
      }

      .skeleton-container.is-not-collapsible .skeleton-icon {
        width: 260px;
        height: 22px;
      }

      .skeleton-content {
        display: grid;
        grid-template-columns: repeat(
          3,
          minmax(0, var(--skeleton-item-size-default))
        );
        gap: var(--skeleton-gap-default);
        row-gap: var(--skeleton-row-gap);
      }

      .skeleton-container.is-not-collapsible .skeleton-content {
        gap: var(--skeleton-gap-collapsible);
        grid-template-columns: repeat(
          3,
          minmax(0, var(--skeleton-item-size-collapsible))
        );
      }

      .skeleton-container.is-not-collapsible
        .skeleton-content
        .skeleton-image-item {
        justify-items: center;
      }

      .skeleton-image-item {
        width: fit-content;
      }

      .skeleton-item-image {
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius);
        height: var(--skeleton-item-size-default);
        width: var(--skeleton-item-size-default);
        margin-bottom: 0.75rem;
      }

      .skeleton-container.is-not-collapsible .skeleton-item-image {
        height: var(--skeleton-item-size-collapsible);
        width: var(--skeleton-item-size-collapsible);
      }

      .skeleton-item-label {
        background: var(--skeleton-gray);
        border-radius: var(--skeleton-border-radius);
        height: 0.75rem;
        width: var(--skeleton-item-size-default);
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        .skeleton-container.is-not-collapsible .skeleton-header {
          flex-direction: row;
          justify-content: space-between;
        }

        .skeleton-content {
          display: flex;
          flex-wrap: wrap;
          grid-template-columns: repeat(
            5,
            minmax(0, var(--skeleton-item-size-default))
          );
        }

        .skeleton-container.is-not-collapsible .skeleton-item-image {
          height: var(--skeleton-item-size-xl);
          width: var(--skeleton-item-size-xl);
        }
      }

      @media screen and (min-width: ${BREAKPOINT_EXTRA_LARGE_PX}px) {
        .skeleton-content {
          grid-template-columns: repeat(
            6,
            minmax(0, var(--skeleton-item-size-default))
          );
        }

        .skeleton-item-image {
          height: var(--skeleton-item-size-xl);
          width: var(--skeleton-item-size-xl);
        }

        .skeleton-item-label {
          width: var(--skeleton-item-size-xl);
        }
      }
    `,
  ];
}
